import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {Location} from '@angular/common';
import { IpmsComponent } from 'src/app/core/linked-tables/_models/ipms-component.model';
import { selectIpmsComponentById, selectIpmsComponentsActionLoading, selectLastCreatedIpmsComponentId } from 'src/app/core/linked-tables/_selectors/ipms-component.selectors';
import { IpmsComponentOnServerCreated, IpmsComponentUpdated } from 'src/app/core/linked-tables/_actions/ipms-component.actions';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-ipms-component-edit',
    templateUrl: './ipms-component-edit.component.html',
    styleUrls: ['./ipms-component-edit.component.scss'],
})


export class IpmsComponentEditComponent implements OnInit, OnDestroy {

    model: IpmsComponent;
    oldModel: IpmsComponent;

    ipmsComponentForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectIpmsComponentsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectIpmsComponentById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initIpmsComponent();
                    }
                });
            } else {
                this.model = new IpmsComponent();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initIpmsComponent();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Interaction types', page: `../default/admin-management/linked-tables/ipms-components`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/ipms-components/edit/${id}`
                    : `../default/admin-management/linked-tables/ipms-components/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initIpmsComponent() {
        this.createForm();
    }


    createForm() {
        this.ipmsComponentForm = this.fb.group({
            key: [this.model.key, Validators.required],
            name: [this.model.name, Validators.required],
            description: [this.model.description, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('IPMS_COMPONENT.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('IPMS_COMPONENT.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.ipmsComponentForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.ipmsComponentForm.markAsPristine();
        this.ipmsComponentForm.markAsUntouched();
        this.ipmsComponentForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.ipmsComponentForm.controls;
        /** check form */
        if (this.ipmsComponentForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateIpmsComponent(this.model);
        } else {
            this.createIpmsComponent(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: IpmsComponentModel
     */
    updateIpmsComponent(_model: IpmsComponent) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateIpmsComponent: Update<IpmsComponent> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new IpmsComponentUpdated({
            partialIpmsComponent: updateIpmsComponent,
            IpmsComponent: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: IpmsComponentModel
     */
    createIpmsComponent(_model: IpmsComponent) {
        this.store.dispatch(new IpmsComponentOnServerCreated({IpmsComponent: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedIpmsComponentId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0 && this.model.key && this.model.key.length > 0 && this.model.description && this.model.description.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name || this.model.key != this.oldModel.key || this.model.description != this.oldModel.description;
        }
        return false;
    }

}
